// src/lib/dropped-save-reporter.js
// 2026-09-27 — репортёр склада несохранённых правок (design v2, P3 брифа).
//
// persistDroppedSave (api-client.js) кладёт запись на склад (`blitz_dropped_saves`) и
// помечает свежую реальную потерю `notice:'new'`. Этот модуль отчитывается сервером
// (POST /api/client/save-dropped) за КАЖДУЮ такую запись — сервер сверяет снимок с диском
// и решает, была ли потеря на самом деле (клиентским ярлыкам не верим, design v2 §1).
//
// Триггеры прогона: событие 'blitz:save-dropped' (сразу, entry — из detail, НЕ повторное
// чтение localStorage: запись могла не пережить localStorage.setItem — H-LC3), старт
// вкладки (installDroppedSaveReporter), вход (App.jsx onLogin зовёт reportFreshDroppedSaves
// напрямую), и раз в 60 секунд, пока на складе есть 'new'.
//
// Ответ сервера решает судьбу записи: lost=0 → удалить (доставилась/перекрыта);
// lost>0 → notice:'confirmed' (data/deleted снимаем — копия уже в audit-журнале сервера);
// 403/400 → удалить (нет прав / сервер отклонил сам отчёт); сеть/401/429/5xx → остаётся
// 'new', заберём на следующем прогоне. Записи без notice (осадок) — никогда не трогаем.

import { API_BASE } from './endpoints.js';
import { authHeaders } from './auth-headers.js';
import { getSessionToken } from '../utils/session.js';
import { isSessionKnownDead } from './session-expiry.js';
import { LS_DROPPED_SAVES_KEY, isIpBanResponse } from './api-client.js';

const REPORT_INTERVAL_MS = 60000;

let installed = false;
let intervalId = null;
let dropListener = null;
let sweepInFlight = false;
// Один запрос в полёте на запись — событие и периодический прогон могут подхватить
// одну и ту же запись почти одновременно (event fires синхронно из persistDroppedSave,
// а reportFreshDroppedSaves могла уже стартовать чуть раньше).
const entriesInFlight = new Set();

function readStore() {
  try {
    const raw = localStorage.getItem(LS_DROPPED_SAVES_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

function writeStore(list) {
  try { localStorage.setItem(LS_DROPPED_SAVES_KEY, JSON.stringify(list)); } catch { /* см. persistDroppedSave — не критично */ }
}

/** Удалить запись (patch=null) или смержить поля в неё; молчит, если записи уже нет. */
function patchStoreEntry(id, patch) {
  const list = readStore();
  const idx = list.findIndex(e => e && e.id === id);
  if (idx === -1) return;
  if (patch === null) list.splice(idx, 1);
  else list[idx] = { ...list[idx], ...patch };
  writeStore(list);
}

function emitChanged() {
  try {
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function' && typeof CustomEvent === 'function') {
      window.dispatchEvent(new CustomEvent('blitz:dropped-saves-changed'));
    }
  } catch {}
}

async function reportEntry(entry) {
  if (!entry || !entry.id || entry.notice !== 'new') return;
  if (entriesInFlight.has(entry.id)) return;
  const token = getSessionToken();
  if (!token || isSessionKnownDead()) return;

  entriesInFlight.add(entry.id);
  try {
    const res = await fetch(`${API_BASE}/client/save-dropped`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        entryId: entry.id,
        endpoint: entry.endpoint,
        reason: entry.reason,
        httpStatus: entry.httpStatus,
        droppedAt: entry.droppedAt,
        data: entry.data,
        deleted: entry.deleted,
      }),
      signal: AbortSignal.timeout(8000),
    });

    // Бан по IP — тоже 403, но это не «нет прав на таблицу»: запись остаётся 'new' до снятия
    // бана, стереть её = потерять единственную копию правки (жюри A 27.09).
    if (res.status === 403 && isIpBanResponse(res)) return;
    if (res.status === 403 || res.status === 400) {
      patchStoreEntry(entry.id, null);
      emitChanged();
      return;
    }
    if (!res.ok) return; // сеть/401/429/5xx — остаётся 'new', следующий прогон повторит

    const json = await res.json().catch(() => null);
    if (!json || typeof json.lost !== 'number') return;

    if (json.lost === 0) {
      patchStoreEntry(entry.id, null);
    } else {
      // data/deleted снимаем — полная копия уже осела в audit-журнале сервера (P2);
      // локально дальше живёт только вердикт, который покажет окно (P5).
      patchStoreEntry(entry.id, {
        notice: 'confirmed',
        data: undefined,
        deleted: undefined,
        verdict: { lost: json.lost, lostRows: json.lostRows, lostDeletes: json.lostDeletes, signature: json.signature },
      });
    }
    emitChanged();
  } catch {
    // сетевой сбой — запись остаётся 'new', следующий прогон повторит
  } finally {
    entriesInFlight.delete(entry.id);
  }
}

/** Прогнать весь склад — только записи 'new'. Один прогон за раз (module-level lock). */
export async function reportFreshDroppedSaves() {
  if (sweepInFlight) return;
  // Нечего слать — не трогаем токен/сессию попусту (склад пуст на большинстве тиков;
  // читать localStorage синхронно дешевле, чем лишний раз спрашивать токен/сессию).
  const entries = readStore().filter(e => e && e.notice === 'new');
  if (entries.length === 0) return;
  sweepInFlight = true;
  try {
    const token = getSessionToken();
    if (!token || isSessionKnownDead()) return;
    for (const entry of entries) {
      await reportEntry(entry);
    }
  } finally {
    sweepInFlight = false;
  }
}

/** Вызывается ОДИН раз из корня App: слушатель события + прогон на старте + 60с тик. */
export function installDroppedSaveReporter() {
  if (installed) return;
  installed = true;

  if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    dropListener = (e) => {
      const entry = e && e.detail && e.detail.entry;
      if (entry) reportEntry(entry);
    };
    window.addEventListener('blitz:save-dropped', dropListener);
  }

  reportFreshDroppedSaves();
  if (typeof setInterval === 'function') {
    intervalId = setInterval(() => { reportFreshDroppedSaves(); }, REPORT_INTERVAL_MS);
  }
}

/** Тестовый сброс модульного состояния (installed-флаг, таймер, in-flight lock'и). */
export function resetDroppedSaveReporterForTests() {
  installed = false;
  sweepInFlight = false;
  entriesInFlight.clear();
  if (intervalId != null) { clearInterval(intervalId); intervalId = null; }
  if (dropListener && typeof window !== 'undefined' && typeof window.removeEventListener === 'function') {
    window.removeEventListener('blitz:save-dropped', dropListener);
  }
  dropListener = null;
}
